/**
 * Shared plumbing for handing an assistant-prepared edit to the editor page it
 * belongs to (the email template editor, the outfitter form builder, etc.).
 *
 * An edit reaches its editor page through one of two channels:
 *  - the **live var**: written when the editor page is already open, so the
 *    mounted editor picks it up reactively.
 *  - **navigation state**: carried through a Gatsby navigation when the editor
 *    page was not open yet and we had to navigate to it.
 *
 * `deliverPendingAssistantEdit` (handler side) chooses the channel; the editor
 * page reads back with `resolvePendingAssistantEdit`.
 */

export function resolvePendingAssistantEdit<TPayload>({
    stateKey,
    pendingFromVar,
    locationState,
    matchesTarget,
    parseNavigationState,
}: {
    readonly stateKey: string
    readonly pendingFromVar: TPayload | null
    readonly locationState: unknown
    readonly matchesTarget: (payload: TPayload) => boolean
    readonly parseNavigationState: (value: unknown) => TPayload | null
}): { payload: TPayload | null; fromNavigationState: boolean } {
    const fromVar =
        pendingFromVar && matchesTarget(pendingFromVar) ? pendingFromVar : null

    const parsed = parseNavigationState(
        readEditFromNavigationState({ locationState, stateKey })
    )
    const fromNavigation = parsed && matchesTarget(parsed) ? parsed : null

    // Prefer the live var (written when the editor page is already open);
    // navigation state is only the handoff used when we had to navigate here.
    return {
        payload: fromVar ?? fromNavigation,
        fromNavigationState: Boolean(fromNavigation),
    }
}

export function deliverPendingAssistantEdit<TPayload>({
    payload,
    stateKey,
    isEditorPageOpen,
    stashForOpenEditor,
    navigateToEditorPage,
}: {
    readonly payload: TPayload
    readonly stateKey: string
    readonly isEditorPageOpen: boolean
    readonly stashForOpenEditor: (payload: TPayload) => void
    readonly navigateToEditorPage: (
        navigationState: Record<string, unknown>
    ) => void
}): void {
    if (isEditorPageOpen) {
        stashForOpenEditor(payload)
        return
    }

    navigateToEditorPage({ [stateKey]: payload })
}

function readEditFromNavigationState({
    locationState,
    stateKey,
}: {
    locationState: unknown
    stateKey: string
}): unknown {
    // Gatsby location state is untyped and can hold arbitrary navigation
    // payloads, so isolate our handoff key before any shape parsing.
    if (
        typeof locationState !== "object" ||
        locationState === null ||
        !Reflect.has(locationState, stateKey)
    ) {
        return undefined
    }

    return Reflect.get(locationState, stateKey)
}
